import type {
  CampaignResponse,
  DeliveryRecord,
  EmailQuotaStatus,
} from "@voice2mail/shared/v2m_api";
import { useCallback, useEffect, useState } from "react";

import * as api from "../api/client";
import { useSortableRows } from "../hooks/useSortableRows";
import { QuotaStatus } from "./QuotaStatus";
import { SortableTh } from "./SortableTh";

interface CampaignProgressProps {
  campaignId: string;
  onBack: () => void;
}

const TD = "px-4 py-2.5 text-sm text-slate-900";

type DeliverySortKey = "email" | "error" | "deliveredAt";
const DELIVERY_ACCESSORS: Record<DeliverySortKey, (d: DeliveryRecord) => string | number | null> = {
  email: (d) => d.email,
  error: (d) => d.error ?? null,
  deliveredAt: (d) => (d.deliveredAt ? d.deliveredAt.getTime() : null),
};

export function CampaignProgress({ campaignId, onBack }: CampaignProgressProps) {
  const [campaign, setCampaign] = useState<CampaignResponse | null>(null);
  const [quota, setQuota] = useState<EmailQuotaStatus | null>(null);
  const [failedDeliveries, setFailedDeliveries] = useState<DeliveryRecord[]>([]);
  const [showFailed, setShowFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  // Default: most-recently-attempted failure first — useful without clicking.
  const deliverySort = useSortableRows(failedDeliveries, DELIVERY_ACCESSORS, {
    key: "deliveredAt",
    direction: "desc",
  });

  const refresh = useCallback(async () => {
    try {
      const [campaignData, quotaData] = await Promise.all([
        api.getCampaign(campaignId),
        api.getEmailQuota(),
      ]);
      setCampaign(campaignData);
      setQuota(quotaData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load progress");
    }
  }, [campaignId]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  const handleViewFailed = async () => {
    if (showFailed) {
      setShowFailed(false);
      return;
    }

    try {
      const result = await api.getDeliveries(campaignId);
      setFailedDeliveries(result.deliveries.filter((d) => d.status === "failed"));
      setShowFailed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load deliveries");
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      // cancelCampaign's response is only {status, id} (see its docstring in
      // api/client.ts) — refetch the full CampaignResponse so counts/status
      // stay accurate instead of clobbering state with a 2-field object.
      await api.cancelCampaign(campaignId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel campaign");
    } finally {
      setCancelling(false);
    }
  };

  if (error && !campaign) {
    return (
      <div>
        <div
          className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4"
          role="alert"
        >
          Error: {error}
        </div>
        <button
          type="button"
          className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
          onClick={onBack}
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-slate-500 text-sm py-6 text-center">Loading campaign progress...</div>
    );
  }

  const processed = campaign.sentCount + campaign.failedCount;
  const progressPercent =
    campaign.totalRecipients > 0 ? Math.round((processed / campaign.totalRecipients) * 100) : 0;
  const isActive = campaign.status === "in_progress" || campaign.status === "dispatching";

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">Campaign Progress</h2>
        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          {campaign.status.replace("_", " ").toUpperCase()}
        </span>
      </div>

      <p className="text-sm text-slate-600">
        Template: <strong>{campaign.template}</strong>
      </p>

      {/* Progress bar */}
      <div>
        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
          <div
            className="h-2 rounded-full bg-blue-500 transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {processed} / {campaign.totalRecipients} ({progressPercent}%)
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{campaign.sentCount}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">Sent</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{campaign.failedCount}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">Failed</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-slate-600">{campaign.pendingCount}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">Pending</div>
        </div>
      </div>

      {/* Quota */}
      <QuotaStatus quota={quota} />

      {/* Actions */}
      <div className="flex gap-3">
        {campaign.failedCount > 0 && (
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            onClick={handleViewFailed}
          >
            {showFailed ? "Hide Failed Deliveries" : "View Failed Deliveries"}
          </button>
        )}
        {isActive && (
          <button
            type="button"
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg transition-colors disabled:bg-slate-300"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? "Cancelling..." : "Cancel Remaining"}
          </button>
        )}
        <button
          type="button"
          className="text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors ml-auto"
          onClick={onBack}
        >
          Back to Campaigns
        </button>
      </div>

      {/* Failed deliveries table */}
      {showFailed && failedDeliveries.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Failed Deliveries ({failedDeliveries.length})
          </h3>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <SortableTh
                      label="Email"
                      sortKey="email"
                      sort={deliverySort.sort}
                      onSort={deliverySort.toggleSort}
                      sticky
                    />
                    <SortableTh
                      label="Error"
                      sortKey="error"
                      sort={deliverySort.sort}
                      onSort={deliverySort.toggleSort}
                    />
                    <SortableTh
                      label="Time"
                      sortKey="deliveredAt"
                      sort={deliverySort.sort}
                      onSort={deliverySort.toggleSort}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {deliverySort.sorted.map((d) => (
                    <tr key={d.userId} className="group">
                      <td className={`${TD} sticky left-0 z-10 bg-white group-hover:bg-slate-50`}>
                        {d.email}
                      </td>
                      <td className={TD}>{d.error || "Unknown error"}</td>
                      <td className={TD}>
                        {d.deliveredAt ? d.deliveredAt.toLocaleTimeString() : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showFailed && failedDeliveries.length === 0 && (
        <p className="text-sm text-slate-500">No failed deliveries found.</p>
      )}
    </div>
  );
}
